import { twoFactorApi } from '@/api/twoFactor';
import { TWO_FACTOR_CONFIG } from '@/config/twoFactorConfig';
import { TwoFactorSetupModal } from '@/features/twoFactor';
import {
  Alert,
  AlertIcon,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';

interface TwoFactorSetupPromptProps {
  isEnabled?: boolean;
}

export const TwoFactorSetupPrompt: React.FC<TwoFactorSetupPromptProps> = ({ isEnabled = true }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: is2FASetupOpen,
    onOpen: on2FASetupOpen,
    onClose: on2FASetupClose,
  } = useDisclosure();
  const [hasChecked, setHasChecked] = useState(false);
  const [has2FAEnabled, setHas2FAEnabled] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!isEnabled || hasChecked) return;

    const checkAndPrompt = async () => {
      try {
        // Check if user has 2FA enabled
        const status = await twoFactorApi.getStatus();
        setHas2FAEnabled(status.enabled);

        const Session = (await import('supertokens-web-js/recipe/session')).default;
        const userIdFromSession = await Session.getUserId();
        setUserId(userIdFromSession);

        if (!status.enabled && userIdFromSession) {
          // Check if user has dismissed the prompt
          const dismissedKey = `${TWO_FACTOR_CONFIG.SETUP_PROMPT_DISMISSED_KEY}_${userIdFromSession}`;
          const lastShownKey = `${TWO_FACTOR_CONFIG.SETUP_PROMPT_LAST_SHOWN_KEY}_${userIdFromSession}`;

          let dismissed = localStorage.getItem(dismissedKey);
          const lastShown = localStorage.getItem(lastShownKey);

          // Only clear localStorage if admin explicitly disabled 2FA after user dismissed
          // This ensures the prompt shows again after admin reset
          if (status.disabledAt && status.disabledAt > 0) {
            const dismissedTime = dismissed ? parseInt(dismissed) : 0;
            const lastShownTime = lastShown ? parseInt(lastShown) : 0;
            const relevantTime = Math.max(dismissedTime, lastShownTime);
            
            if (status.disabledAt > relevantTime) {
              localStorage.removeItem(dismissedKey);
              localStorage.removeItem(lastShownKey);
              dismissed = null;
            }
          }

          if (!dismissed) {
            const now = Date.now();
            const sevenDays = 7 * 24 * 60 * 60 * 1000;

            // Only show if never shown or more than 7 days since last "Remind me later"
            if (!lastShown || now - parseInt(lastShown) > sevenDays) {
              onOpen();
            }
          }
        }
      } catch (error) {
        console.error('Error checking 2FA status:', error);
      } finally {
        setHasChecked(true);
      }
    };

    checkAndPrompt();
  }, [isEnabled, hasChecked, onOpen]);

  const handleSetupNow = () => {
    onClose();
    on2FASetupOpen();
  };

  const handleRemindLater = () => {
    if (userId) {
      const lastShownKey = `${TWO_FACTOR_CONFIG.SETUP_PROMPT_LAST_SHOWN_KEY}_${userId}`;
      localStorage.setItem(lastShownKey, Date.now().toString());
    }
    onClose();
  };

  const handleDismiss = () => {
    if (userId) {
      const dismissedKey = `${TWO_FACTOR_CONFIG.SETUP_PROMPT_DISMISSED_KEY}_${userId}`;
      localStorage.setItem(dismissedKey, Date.now().toString());
    }
    onClose();
  };

  const handle2FAComplete = () => {
    setHas2FAEnabled(true);
    on2FASetupClose();
  };

  if (!isEnabled || has2FAEnabled) {
    return null;
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleRemindLater} isCentered>
        <ModalOverlay />
        <ModalContent px={3} py={3}>
          <ModalHeader>Secure Your Account with 2FA</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Two-factor authentication adds an extra layer of security to your account.
                </Text>
              </Alert>

              <Text>
                We recommend enabling two-factor authentication (2FA) to protect your account from
                unauthorized access.
              </Text>

              <Text fontSize="sm">
                You'll need an authenticator app like Google Authenticator, Authy, 1Password, or
                similar.
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <VStack spacing={3} align="stretch" w="full">
              <Button variant="primary" onClick={handleSetupNow} fontWeight="bold">
                Set up now
              </Button>
              <Flex gap={3} flexDir={{ base: 'column', md: 'row' }}>
                <Button
                  variant="outline"
                  colorScheme="black"
                  w="100%"
                  _hover={{ opacity: 0.5 }}
                  onClick={handleDismiss}
                >
                  Don't show again
                </Button>
                <Button
                  variant="outline"
                  colorScheme="black"
                  w="100%"
                  _hover={{ opacity: 0.5 }}
                  onClick={handleRemindLater}
                >
                  Remind me later
                </Button>
              </Flex>
            </VStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <TwoFactorSetupModal
        isOpen={is2FASetupOpen}
        onClose={on2FASetupClose}
        onComplete={handle2FAComplete}
        allowSkip={false}
      />
    </>
  );
};
